import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { SwiperSlide } from 'swiper/react';
import type Swiper from 'swiper';
import { DialogContent } from '@mui/material';
import { MappedAEMProperties } from '../../../../types';
import styles from './LoginDialogBox.module.scss';
import { getNotificationState, storeNotificationState } from './LoginDialogState';
import { Label } from '../../../common/Label/Label';
import BaseDialog from '../../../common/BaseDialog/BaseDialog';
import BaseDialogHeading from '../../../common/BaseDialog/BaseDialogHeading';
import Carousel from '../../base/Carousel/Carousel';
import PureImage from '../../../common/Image/PureImage';
import Checkbox from '../../../common/Checkbox/Checkbox';
import Layout from '../../../common/Layout/Layout';
import { type Asset } from '../../../common/model';
import { useConfig } from '../../../../lib/hooks/config';
import { LOGIN_DIALOG_BOX_LABELS } from '../../../../lib/constants/labelKeys';
import { DATE_FORMAT_FALLBACK } from '../../../../lib/constants/globals';

export type AemNotification = {
  uniqueId: string;
  content: string;
  title?: string;
  image?: Asset;
  lastPublished: number;
};

export type LoginDialogBoxProps = {
  notifications?: AemNotification[];
} & MappedAEMProperties;

const LoginDialogBox = ({ isInEditor, notifications }: LoginDialogBoxProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [suppressDialog, setSuppressDialog] = useState(false);
  const { globalDateFormat = DATE_FORMAT_FALLBACK } = useConfig() ?? {};

  const digestMessage = async (message: string): Promise<string> => {
    const encodedMessage = new TextEncoder().encode(message);
    const hashedMessage = await crypto.subtle.digest('SHA-256', encodedMessage);
    const hashArray = Array.from(new Uint8Array(hashedMessage));

    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const hashNotifications = async (notificationsToHash: AemNotification[]): Promise<string> => {
    const sortedNotifications = notificationsToHash
      .map(({ uniqueId, ...rest }) => rest)
      .map(el => Object.fromEntries(Object.entries(el).sort()));
    const stringifiedNotifications = JSON.stringify(sortedNotifications);

    if (window.isSecureContext) {
      return digestMessage(stringifiedNotifications);
    }

    return stringifiedNotifications;
  };

  const handleClose = async () => {
    setIsOpen(false);

    if (suppressDialog && notifications) {
      const hashedNotifications = hashNotifications(notifications);

      storeNotificationState(await hashedNotifications);
    }
  };

  const handleSlideChange = (swiper: Swiper) => {
    setCurrentSlide(swiper.activeIndex);
  };

  const handleSuppressCheckbox = () => {
    setSuppressDialog(!suppressDialog);
  };

  useEffect(() => {
    const checkNotificationsState = async () => {
      const savedHash = getNotificationState();
      const hashedNotifications = await hashNotifications(notifications || []);

      setShouldShowDialog(savedHash !== hashedNotifications);
    };

    checkNotificationsState();
  }, [notifications]);

  if (isInEditor) {
    return (
      <Layout className={styles.authoringMessage}>
        Login notifications configured in the Global Configuration content fragment will be displayed in a dialog.
      </Layout>
    );
  }

  if (!notifications || notifications.length === 0 || !shouldShowDialog) {
    return null;
  }

  return (
    <BaseDialog open={isOpen} onClose={handleClose} classes={{ paper: styles.dialog }}>
      <BaseDialogHeading
        titleProps={{ className: styles.titleText }}
        className={styles.titleRow}
        closeButtonProps={{ onClick: handleClose }}
      >
        {notifications[currentSlide].title || ''}
      </BaseDialogHeading>
      <DialogContent className={styles.mainContent}>
        <Carousel
          ariaLabel="Latest notifications"
          id="login-dialog-box"
          slidesPerView={1}
          onSlideChange={handleSlideChange}
          showCarouselOnMobile
        >
          {notifications.map(({ uniqueId: id, content, image, lastPublished }) => (
            <SwiperSlide key={id}>
              {image && <PureImage src={image.path} />}
              <div className={styles.slideContent}>
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: content }} />
                <div className={styles.publishedDate}>
                  <Label name={LOGIN_DIALOG_BOX_LABELS.POSTED} />: {dayjs(lastPublished).format(globalDateFormat)}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Carousel>
        <div className={styles.bottomRow}>
          <Checkbox
            label={<Label name={LOGIN_DIALOG_BOX_LABELS.DONT_SHOW_AGAIN} />}
            selected={suppressDialog}
            onSelect={handleSuppressCheckbox}
            className={styles.suppressCheckbox}
            customClasses={{ label: styles.suppressCheckboxLabel }}
          />
          <div className={styles.slideCount}>
            {currentSlide + 1} of {notifications.length}
          </div>
        </div>
      </DialogContent>
    </BaseDialog>
  );
};

export default LoginDialogBox;
